import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  RuleConfiguration,
  GetAllRulesResponse,
  EarnRule,
  ValidityRule,
  RedemptionRule,
} from "@/api/giftcard.api";
import { getFormattedDateTime } from "@/utils";
import { Edit } from "lucide-react";
import { CreateRuleForm } from "./CreateRuleForm";

interface ViewRuleModalProps {
  rule: RuleConfiguration;
  rulesData: GetAllRulesResponse;
  isOpen: boolean;
  onClose: () => void;
  onUpdate?: () => void;
}

export function ViewRuleModal({
  rule,
  rulesData,
  isOpen,
  onClose,
  onUpdate,
}: ViewRuleModalProps) {
  const [activeTab, setActiveTab] = useState<string>("earn");
  const [isEditMode, setIsEditMode] = useState(false);

  const formatDate = (dateString: string): string => {
    if (!dateString || dateString === "0001-01-01T00:00:00") {
      return "-";
    }
    return getFormattedDateTime(dateString) || "-";
  };

  // Find matching rules by ruleId from respective API lists
  const configurationRuleId = rule.ruleId;
  const earnRule = (rulesData.listEarnRule ?? []).find(
    (r) => r.ruleId === configurationRuleId
  );
  const validityRule = (rulesData.listValidityRule ?? []).find(
    (r) => r.ruleId === configurationRuleId
  );
  const redemptionRule = (rulesData.listRedemptionRule ?? []).find(
    (r) => r.ruleId === configurationRuleId
  );

  // Format points issuance type for display
  const formatPointsIssuanceType = (type: string): string => {
    const typeMap: Record<string, string> = {
      "%OfInvoiceAmount": "% of Invoice Amount",
      Percentage: "Percentage",
      FixedPointsPerTxn: "Fixed point per transaction",
      FixedPoints: "Fixed Points",
      FixedPoints1: "Fixed Points 1",
    };
    return typeMap[type] || type;
  };

  // Format redemption criteria for display
  const formatRedemptionCriteria = (criteria: string): string => {
    const criteriaMap: Record<string, string> = {
      "%OfInvoiceAmount": "% of Invoice Amount",
      "%OfAvailablePoints": "% Available Points",
      NoCriteria: "No Criteria",
    };
    return criteriaMap[criteria] || criteria;
  };

  // Format earn while redeem for display
  const formatEarnWhileRedeem = (earn: string): string => {
    const earnMap: Record<string, string> = {
      PartialEarn: "Partial Earn",
      FullEarn: "Full Earn",
      NoEarn: "No Earn",
      YES: "Yes",
      NO: "No",
    };
    return earnMap[earn] || earn;
  };

  // Determine which tabs to show
  const tabs = [];
  if (rule.isEarnRule) tabs.push({ id: "earn", label: "Earn Rule" });
  if (rule.isValidityRule) tabs.push({ id: "validity", label: "Validity Rule" });
  if (rule.isRedemptionRule)
    tabs.push({ id: "redemption", label: "Redemption Rule" });

  // Set initial tab to first available
  React.useEffect(() => {
    if (tabs.length > 0 && !tabs.find((t) => t.id === activeTab)) {
      setActiveTab(tabs[0].id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rule.ruleId]);

  // Prepare initial values for edit form
  const initialFormValues = {
    ruleId: rule.ruleId,
    title: rule.title,
    description: "",
    earnRule: earnRule
      ? {
          pointsIssuanceType: earnRule.pointsIssuanceType,
          minInvoiceAmount: earnRule.minInvoiceAmt,
          awardPoints: earnRule.awardPoints,
        }
      : undefined,
    validityRule: validityRule
      ? {
          pointsValidityType: validityRule.pointsValidityType,
          pointsValidityDays: validityRule.pointsValidityDays,
        }
      : undefined,
    redemptionRule: redemptionRule
      ? {
          minInvoiceAmount: redemptionRule.burnMinInvoiceAmt,
          maxRedemptionPoints: redemptionRule.maxRedemptionPoints,
          redemptionCriteria: redemptionRule.redemptionCriteria,
          earnWhileRedeem: redemptionRule.earnWhileRedeem,
        }
      : undefined,
  };

  const handleClose = () => {
    setIsEditMode(false);
    onClose();
  };

  if (isEditMode) {
    return (
      <Dialog open={isOpen} onOpenChange={handleClose}>
        <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Rule: {rule.title}</DialogTitle>
          </DialogHeader>
          <CreateRuleForm
            editMode={true}
            initialValues={initialFormValues}
            onSuccess={() => {
              setIsEditMode(false);
              if (onUpdate) {
                onUpdate();
              }
            }}
            onCancel={() => {
              setIsEditMode(false);
            }}
          />
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center justify-between pr-8">
            <DialogTitle>Rule Details: {rule.title}</DialogTitle>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsEditMode(true)}
              className="flex items-center gap-2"
            >
              <Edit className="h-4 w-4" />
              Edit
            </Button>
          </div>
        </DialogHeader>

        <div className="space-y-6">
          {/* Rule Configuration Info */}
          <div className="space-y-2">
            <h3 className="text-lg font-semibold">Rule Information</h3>
            <div className="grid grid-cols-2 gap-4 p-4 bg-muted rounded-md">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Title</p>
                <p className="text-sm">{rule.title}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">ID</p>
                <p className="text-sm">{rule.ruleId}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  Created Date
                </p>
                <p className="text-sm">{formatDate(rule.createddatetime)}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  Updated Date
                </p>
                <p className="text-sm">{formatDate(rule.updatedDatetime)}</p>
              </div>
            </div>
          </div>

          {/* Tabs for Rule Details */}
          {tabs.length > 0 && (
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList
                className={`grid w-full ${
                  tabs.length === 1
                    ? "grid-cols-1"
                    : tabs.length === 2
                    ? "grid-cols-2"
                    : "grid-cols-3"
                }`}
              >
                {tabs.map((tab) => (
                  <TabsTrigger key={tab.id} value={tab.id}>
                    {tab.label}
                  </TabsTrigger>
                ))}
              </TabsList>

              {/* Earn Rule Tab */}
              {rule.isEarnRule && (
                <TabsContent value="earn" className="space-y-4 mt-6">
                  <div>
                    <h3 className="text-lg font-semibold mb-4">Earn Rule Details</h3>
                    {earnRule ? (
                      <div className="space-y-4 p-4 bg-muted rounded-md">
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <p className="text-sm font-medium text-muted-foreground">
                              Points Issuance Type
                            </p>
                            <p className="text-sm">
                              {formatPointsIssuanceType(earnRule.pointsIssuanceType)}
                            </p>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-muted-foreground">
                              Minimum Invoice Amount
                            </p>
                            <p className="text-sm">{earnRule.minInvoiceAmt}</p>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-muted-foreground">
                              Award Points
                            </p>
                            <p className="text-sm">{earnRule.awardPoints}</p>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground p-4">
                        No earn rule details found.
                      </p>
                    )}
                  </div>
                </TabsContent>
              )}

              {/* Validity Rule Tab */}
              {rule.isValidityRule && (
                <TabsContent value="validity" className="space-y-4 mt-6">
                  <div>
                    <h3 className="text-lg font-semibold mb-4">
                      Validity Rule Details
                    </h3>
                    {validityRule ? (
                      <div className="space-y-4 p-4 bg-muted rounded-md">
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <p className="text-sm font-medium text-muted-foreground">
                              Points Validity Type
                            </p>
                            <p className="text-sm">
                              {validityRule.pointsValidityType}
                            </p>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-muted-foreground">
                              Points Validity Days
                            </p>
                            <p className="text-sm">
                              {validityRule.pointsValidityDays} days
                            </p>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground p-4">
                        No validity rule details found.
                      </p>
                    )}
                  </div>
                </TabsContent>
              )}

              {/* Redemption Rule Tab */}
              {rule.isRedemptionRule && (
                <TabsContent value="redemption" className="space-y-4 mt-6">
                  <div>
                    <h3 className="text-lg font-semibold mb-4">
                      Redemption Rule Details
                    </h3>
                    {redemptionRule ? (
                      <div className="space-y-4 p-4 bg-muted rounded-md">
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <p className="text-sm font-medium text-muted-foreground">
                              Burn Minimum Invoice Amount
                            </p>
                            <p className="text-sm">
                              {redemptionRule.burnMinInvoiceAmt}
                            </p>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-muted-foreground">
                              Max Redemption Points
                            </p>
                            <p className="text-sm">
                              {redemptionRule.maxRedemptionPoints}
                            </p>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-muted-foreground">
                              Redemption Criteria
                            </p>
                            <p className="text-sm">
                              {formatRedemptionCriteria(
                                redemptionRule.redemptionCriteria
                              )}
                            </p>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-muted-foreground">
                              Earn While Redeem
                            </p>
                            <p className="text-sm">
                              {formatEarnWhileRedeem(
                                redemptionRule.earnWhileRedeem
                              )}
                            </p>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground p-4">
                        No redemption rule details found.
                      </p>
                    )}
                  </div>
                </TabsContent>
              )}
            </Tabs>
          )}

          {tabs.length === 0 && (
            <p className="text-sm text-muted-foreground p-4">
              No rule details available.
            </p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
